import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { UpsellCartEntry } from '@fhs/cart';
import { useCartActions } from '@fhs/cart/src/state/cart-store';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import SectionHeading from 'pages/cart/section-heading';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';

import { UpsellItems, UpsellSectionContainer, UpsellTitle } from './styles';
import { IUpsellProps } from './types';
import UpsellItem from './upsell-item';
import { UpsellSkeleton } from './upsell.skeleton';

export const Upsell: React.FC<IUpsellProps> = ({ isInCart, items, loading }) => {
  const { logRBIEvent } = useCRMEventsContext();

  const { addItemToCart } = useCartActions();

  const { formatMessage } = useIntl();

  const [orderAddedAlertMessage, setOrderAddedAlertMessage] = useState('');

  const handleAddToCart = useCallback(
    (item: UpsellCartEntry) => {
      // Strip off nutrition property because apollo complains nutrition is not in the expected type schema
      const { nutrition, ...cartEntryWithoutNutrition } = item;
      addItemToCart(cartEntryWithoutNutrition);

      const upsellItemIndex = items.findIndex(upsellItem => upsellItem.sanityId === item.sanityId);
      const upsellItemPosition = upsellItemIndex >= 0 ? upsellItemIndex + 1 : undefined;

      logRBIEvent({
        name: CustomEventNames.UPSELL_ADDED,
        type: EventTypes.Other,
        attributes: {
          name: item.name,
          sanityId: item.sanityId,
          ...(item.price && { price: item.price / 100 }),
          upsellItemPosition,
        },
      });

      setOrderAddedAlertMessage(formatMessage({ id: 'itemAddedToCart' }, { itemName: item.name }));
    },
    [addItemToCart, formatMessage, items, logRBIEvent]
  );

  if (!items.length && !loading) {
    return null;
  }

  const title = formatMessage({ id: 'addToOrderHeader' });

  return (
    <UpsellSectionContainer testID="upsell">
      {isInCart ? (
        <SectionHeading testID="add-to-order-header">{title}</SectionHeading>
      ) : (
        <UpsellTitle>{title}</UpsellTitle>
      )}
      {loading && !items.length ? (
        <UpsellSkeleton />
      ) : (
        <UpsellItems>
          {items.map(item => (
            <UpsellItem key={item.sanityId} item={item} addToCard={handleAddToCart} />
          ))}
        </UpsellItems>
      )}
      {orderAddedAlertMessage.length > 0 && (
        <VisuallyHidden role="alert" accessibilityLabel={orderAddedAlertMessage} />
      )}
    </UpsellSectionContainer>
  );
};
